/**
 * Shared sponsor-redemption constants for the web app.
 *
 * These mirror the server rule in `apps/uninfo-api/src/config/sponsorQuantity.js`
 * purely so the UI can give live feedback while the student types. The server
 * stays authoritative and re-validates both fields on every redemption, so a
 * drift here can never widen what a student is actually allowed to claim — it
 * can only make the hint wrong for a moment.
 *
 * `computeMaxQuantity` is a deliberate duplicate of the server function rather
 * than a shared package import: the two deploy independently and the web bundle
 * must not depend on the API workspace.
 */

/**
 * Minimum committee Instagram accounts required for a SOCIETY redemption.
 *
 * Society-only: a personal activity has no society and so no committee, and the
 * server never enforces a minimum on either field. The personal path asks for the
 * official account alone, which is what `sponsorRequirements` resolves a
 * `register_app` task from for a personal activity.
 */
export const MIN_COMMITTEE_IGS = 5;

/** Maximum committee Instagram accounts the form collects. Society-only too. */
export const MAX_COMMITTEE_IGS = 10;

/** Minimum available sponsor tasks required to redeem. */
export const MIN_AVAILABLE_SPONSOR_TASKS = 4;

/** How much sponsor value in HKD one participant is entitled to. */
export const QUANTITY_PER_PARTICIPANT_HKD = 1;

/**
 * Upper bound on the self-reported participant count.
 *
 * This is the capacity of the `participants_quantity` column, an `Int`, not a policy
 * limit. Mirror of the server's `MAX_PARTICIPANTS`; the server stays authoritative.
 */
export const MAX_PARTICIPANTS = 2147483647;

/**
 * Fallback ceiling when a product has no usable price.
 *
 * Deliberately its own number rather than the participant bound: those used to be the
 * same constant, so relaxing the participant count would have quietly raised how much
 * of a priceless product a student could claim.
 */
export const NO_PRICE_MAX_QUANTITY = 500;

/**
 * Mirror of the server's `computeMaxQuantity`.
 *
 * The budget is `participants x HK$1`, so the ceiling is `ceil(budget / price)`
 * in PACKS as priced. A missing, unparseable or non-positive price means the
 * product has no value cap.
 *
 * @param participants self-reported participant count
 * @param price unit price of ONE PACK in HKD, or null when unknown
 * @returns the largest quantity the student may pick, or 0 when the participant
 *   count is itself invalid
 */
export const computeMaxQuantity = (
  participants: number | string,
  price?: number | string | null,
): number => {
  const value =
    typeof participants === "string" ? Number(participants.trim()) : participants;
  if (
    !Number.isFinite(value) ||
    !Number.isInteger(value) ||
    value < 1 ||
    value > MAX_PARTICIPANTS
  ) {
    return 0;
  }
  const budgetHkd = value * QUANTITY_PER_PARTICIPANT_HKD;
  if (price === null || price === undefined || price === "") return NO_PRICE_MAX_QUANTITY;
  let parsed: number | null = null;
  if (typeof price === "number") {
    parsed = Number.isFinite(price) ? price : null;
  } else if (typeof price === "string") {
    const trimmed = price.trim();
    if (trimmed !== "") {
      const p = Number(trimmed);
      parsed = Number.isFinite(p) ? p : null;
    }
  } else if (typeof price === "object") {
    if (typeof (price as any).toNumber === "function") {
      const p = (price as any).toNumber();
      parsed = Number.isFinite(p) ? p : null;
    } else {
      const p = Number(String(price));
      parsed = Number.isFinite(p) ? p : null;
    }
  }
  if (parsed === null || parsed <= 0) return NO_PRICE_MAX_QUANTITY;
  return Math.max(1, Math.ceil(budgetHkd / parsed));
};

/** Format a HKD amount the way the agreement and the modal both print it. */
export const formatHkd = (value: number): string => `HK$${value.toFixed(2)}`;